/*
 * <mega-menu-visual>
 *
 * Drives the visual mega menu: tab switching (left rail -> right panel), hover-to-open
 * for desktop pointers, a sliding background indicator that follows the hovered/active
 * tab, and re-running the open reveal animation on every open. Opening/closing is
 * otherwise owned by the surrounding <header-menu>. WAI-ARIA tablist (roving tabindex +
 * arrow keys, activation follows focus).
 */
if (!customElements.get('mega-menu-visual')) {
  customElements.define(
    'mega-menu-visual',
    class MegaMenuVisual extends HTMLElement {
      constructor() {
        super();
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.indicator = this.querySelector('.mega-menu-visual__rail-indicator');
        this.details = this.closest('details');
        this.content = this.closest('.mega-menu__content');
        this.closeTimer = null;
        // When opening as a direct hand-off from another open mega menu, skip the
        // fade-in reveal so the page behind never flashes between the two panels.
        this.openInstant = false;
        this.activeTab = this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0];
      }

      connectedCallback() {
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', (event) => {
            event.preventDefault();
            this.activate(index, true);
          });
          tab.addEventListener('mouseenter', () => this.activate(index, false));
          tab.addEventListener('focus', () => this.activate(index, false));
          tab.addEventListener('keydown', (event) => this.onKeyDown(event, index));
        });

        // The sliding indicator stays anchored to the ACTIVE panel tab (the one whose
        // panel is showing). Hovering a panel tab activates it, so the pill still slides
        // on tab-swap; hovering a direct-link tab leaves it put (link tabs get their own
        // hover highlight via CSS).
        if (this.details) this.details.addEventListener('toggle', () => this.onDetailsToggle());

        this.setupHoverOpen();
      }

      // Re-run the reveal on every open (a native <details> won't reliably restart a
      // CSS animation on re-open), and place the indicator once the rail is measurable.
      onDetailsToggle() {
        if (this.details.open) {
          this.details.classList.remove('is-closing');
          this.details.classList.remove('is-opening');
          if (this.openInstant) {
            // Hand-off from another mega menu: open with no reveal, so the incoming
            // panel is opaque immediately and there's no transparent gap to flash through.
            this.openInstant = false;
          } else {
            void this.details.offsetWidth; // force reflow so the animation restarts
            this.details.classList.add('is-opening');
          }
          this.moveIndicator(this.activeTab, false);
        } else {
          this.details.classList.remove('is-opening');
          this.openInstant = false;
        }
      }

      // Open SHOP on hover for desktop pointers. Keyboard and click still work.
      setupHoverOpen() {
        if (!this.details) return;
        if (!window.matchMedia('(min-width: 990px) and (hover: hover)').matches) return;

        const summary = this.details.querySelector('summary');

        [summary, this.content].forEach((el) => {
          if (!el) return;
          el.addEventListener('mouseenter', () => this.openMenu());
          el.addEventListener('mouseleave', () => this.scheduleClose());
        });

        if (summary) {
          summary.addEventListener('click', (event) => {
            if (event.detail > 0) {
              event.preventDefault();
              this.openMenu();
            }
          });
        }
      }

      openMenu() {
        this.cancelClose();
        if (this.details) this.details.classList.remove('is-closing');
        // Close any other open visual mega menu immediately, so its caret and panel
        // don't lag behind while this one opens. If one WAS open, treat this as a
        // hand-off and open instantly (no reveal) to avoid the flash between panels.
        let handoff = false;
        document.querySelectorAll('mega-menu-visual').forEach((other) => {
          if (other === this || typeof other.closeNow !== 'function') return;
          if (other.details && other.details.open) handoff = true;
          other.closeNow();
        });
        this.openInstant = handoff;
        if (this.details && !this.details.open) this.details.open = true;
      }

      // Snap closed now (no fade) — used when another menu opens.
      closeNow() {
        this.cancelClose();
        if (!this.details || !this.details.open) return;
        this.details.classList.remove('is-closing');
        this.details.classList.remove('is-opening');
        this.details.open = false;
      }

      scheduleClose() {
        this.cancelClose();
        this.closeTimer = setTimeout(() => this.animateClose(), 200);
      }

      // Subtle fade-out (mirrors the open reveal) before the panel actually closes.
      animateClose() {
        if (!this.details || !this.details.open) return;
        this.details.classList.remove('is-opening');
        this.details.classList.add('is-closing');
        this.closeTimer = setTimeout(() => {
          if (!this.details) return;
          this.details.classList.remove('is-closing');
          this.details.open = false;
        }, 200);
      }

      cancelClose() {
        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }
      }

      // Slides/resizes the background indicator to sit behind the given rail item.
      moveIndicator(item, animate) {
        if (!this.indicator) return;
        if (!item) {
          this.indicator.classList.remove('is-visible');
          return;
        }
        if (animate === false) this.indicator.style.transition = 'none';
        this.indicator.style.width = `${item.offsetWidth}px`;
        this.indicator.style.height = `${item.offsetHeight}px`;
        this.indicator.style.transform = `translate(${item.offsetLeft}px, ${item.offsetTop}px)`;
        this.indicator.classList.add('is-visible');
        if (animate === false) {
          void this.indicator.offsetWidth; // commit the jump before re-enabling transitions
          this.indicator.style.transition = '';
        }
      }

      onKeyDown(event, index) {
        let next = null;

        switch (event.key) {
          case 'ArrowDown':
          case 'ArrowRight':
            next = (index + 1) % this.tabs.length;
            break;
          case 'ArrowUp':
          case 'ArrowLeft':
            next = (index - 1 + this.tabs.length) % this.tabs.length;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = this.tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.activate(next, true);
      }

      activate(index, moveFocus) {
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
          tab.classList.toggle('is-active', selected);

          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.classList.toggle('is-active', selected);
        });

        this.activeTab = this.tabs[index];
        this.moveIndicator(this.activeTab, true);

        if (moveFocus && this.tabs[index]) this.tabs[index].focus();
      }
    }
  );
}
